// Auto-generated from mortgage_loan.xsd v5.248
// TypeScript models for Mortgage Loan

// Base enums
export enum BaseYN {
    Y = 'Y',
    N = 'N'
}

export enum InterviewMethod {
    TELEPHONE = 'TELEPHONE',
    FACETOFACE = 'FACETOFACE',
    INTERNET = 'INTERNET',
    MAIL = 'MAIL',
    FAX = 'FAX'
}

export enum CreditReportFormat {
    RAW_XML = 'RAW_XML',
    MCL_COMMON = 'MCL_COMMON',
    MISMO = 'MISMO'
}

export enum CreditReportEncoding {
    CDATA = 'CDATA',
    INNER_TEXT = 'INNER_TEXT',
    BASE64 = 'BASE64'
}

export enum PropertyTypeOwned {
    PR = 'PR',  // Primary Residence
    SH = 'SH',  // Second Home
    IP = 'IP'   // Investment Property
}

export enum TitleHoldType {
    S = 'S',    // Solely
    SP = 'SP',  // Joint w/ Spouse
    O = 'O'     // Joint w/ another person
}

export enum RateType {
    F = 'F',    // Fixed
    A = 'A'     // Adjustable
}

export enum MortgageLoanInfoRateType {
    F = 'F',    // Fixed
    A = 'A',    // Adjustable
    G = 'G',    // GPM
    O = 'O'     // Other
}

export enum SpecialIncomeType {
    AlimonyChildSupport = 'AlimonyChildSupport',
    AutomobileExpenseAccount = 'AutomobileExpenseAccount',
    FosterCare = 'FosterCare',
    NotesReceivableInstallment = 'NotesReceivableInstallment',
    Pension = 'Pension',
    SocialSecurity = 'SocialSecurity',
    Trust = 'Trust',
    Unemployment = 'Unemployment',
    VABenefitsNonEducational = 'VABenefitsNonEducational',
    MortgageDifferential = 'MortgageDifferential',
    MilitaryBasePay = 'MilitaryBasePay',
    MilitaryRationsAllowance = 'MilitaryRationsAllowance',
    MilitaryFlightPay = 'MilitaryFlightPay',
    MilitaryHazardPay = 'MilitaryHazardPay',
    MilitaryClothesAllowance = 'MilitaryClothesAllowance',
    MilitaryQuartersAllowance = 'MilitaryQuartersAllowance',
    MilitaryPropPay = 'MilitaryPropPay',
    MilitaryOverseasPay = 'MilitaryOverseasPay',
    MilitaryCombatPay = 'MilitaryCombatPay',
    MilitaryVariableHousingAllowance = 'MilitaryVariableHousingAllowance',
    SubjectPropertyNetCashFlow = 'SubjectPropertyNetCashFlow'
}

export enum MortgageInsuranceType {
    CREDIT_LIFE = 'CREDIT_LIFE',
    CREDIT_DISABILITY = 'CREDIT_DISABILITY',
    DEBT_CANCELLATION = 'DEBT_CANCELLATION'
}

export enum OtherCreditType {
    CASH_DEPOSIT_ON_SALES_CONTRACT = 'CASH DEPOSIT ON SALES CONTRACT',
    SELLER_CREDIT = 'SELLER CREDIT',
    LENDER_CREDIT = 'LENDER CREDIT',
    RELOCATION_FUNDS = 'RELOCATION FUNDS',
    EMPLOYER_ASSISTED_HOUSING = 'EMPLOYER ASSISTED HOUSING',
    LEASE_PURCHASE_FUND = 'LEASE PURCHASE FUND',
    OTHER = 'OTHER',
    BORROWER_PAID_FEES = 'BORROWER PAID FEES'
}

// Housing Expense
export interface HousingExpense {
    monthly_payment?: number;
    other_pi?: number;
    hazard_insurance?: number;
    real_estate_tax?: number;
    mortgage_insurance?: number;
    hoa_due?: number;
    other_expense?: number;
    flood_insurance?: number;
    flood_is_escrow?: BaseYN;
    hazard_is_escrow?: BaseYN;
    homeowners_insurance?: number;
}

// Declaration
export interface MortgageDeclaration {
    // Main declaration fields
    has_outstanding_judgement?: BaseYN;
    has_bankruptcy?: BaseYN;
    has_bad_loan?: BaseYN;
    is_in_lawsuit?: BaseYN;
    is_related_to_bad_loan?: BaseYN;
    is_presently_delinquent?: BaseYN;
    has_separate_maintenance?: BaseYN;
    is_down_payment_borrowed?: BaseYN;
    is_endorser_on_note?: BaseYN;
    has_owner_ship_interest?: BaseYN;
    type_of_property_owned?: PropertyTypeOwned;
    is_property_primary_residence?: BaseYN;

    // Explanation fields
    has_outstanding_judgement_explanation?: string;
    has_bankruptcy_explanation?: string;
    has_bad_loan_explanation?: string;
    is_in_lawsuit_explanation?: string;
    is_related_to_bad_loan_explanation?: string;
    is_presently_delinquent_explanation?: string;
    has_separate_maintenance_explanation?: string;
    is_down_payment_borrowed_explanation?: string;
    is_endorser_on_note_explanation?: string;

    // Additional declaration fields
    has_judge_bankrupt_foreclosure?: BaseYN;
    has_declare_bankrupt?: BaseYN;
    has_chapter_13?: BaseYN;
    has_lawsuit_party?: BaseYN;
    has_judgement?: BaseYN;
    has_foreclosure?: BaseYN;
    has_reposession?: BaseYN;
    has_other_obligation?: BaseYN;
    has_past_due_bills?: BaseYN;
    has_co_maker?: BaseYN;
    co_maker_primary_name?: string;
    co_maker_creditor?: string;
    co_maker_amount?: number;
    has_alias?: BaseYN;
    alias?: string;
    has_income_decline?: BaseYN;
    has_suits_pending?: BaseYN;
    has_alimony?: BaseYN;
    alimony_recipient?: string;
    alimony_recipient_address?: string;
    has_property_for_sale?: BaseYN;
}

// Address
export interface BaseAddress {
    street_address_1?: string;
    street_address_2?: string;
    street_address_3?: string;
    city?: string;
    state?: string;
    zip?: string;
    county?: string;
    country?: string;
}

export interface CurrentAddress extends BaseAddress {
    occupancy_status?: string;
    occupancy_months?: number;
    monthly_rent?: number;
}

export interface MailingAddress extends BaseAddress {
    is_current?: BaseYN;
}

// Employment
export interface BaseEmployment {
    employer?: string;
    occupation?: string;
    employment_start_date?: Date;
    employed_months?: number;
    employment_address?: string;
    employment_city?: string;
    employment_state?: string;
    employment_zip?: string;
    employment_phone?: string;
    supervisor_name?: string;
    pay_grade?: string;
    ets?: Date;
    employment_weekly_hours?: number;
    employment_business_type?: string;
}

export interface CurrentEmployment extends BaseEmployment {
    employee_of_lender_type?: string;
    is_employee_of_lender?: BaseYN;
    profession_months?: number;
}

export interface PreviousEmployment extends BaseEmployment {
    monthly_income?: number;
    employment_end_date?: Date;
}

// Income
export interface SpecialIncome {
    income_type?: SpecialIncomeType;
    amount?: number;
}

export interface MonthlyIncome {
    base_income?: number;
    overtime?: number;
    bonus?: number;
    commission?: number;
    dividends_interest?: number;
    net_rental_income?: number;
    other_income?: number;
    other_income_description?: string;
    SPECIAL_INCOME?: SpecialIncome[];
}

// Monthly Debt
export interface MonthlyDebt {
    monthly_liability?: number;
    monthly_housing_cost?: number;
    monthly_rent?: number;
    monthly_mortgage_payment?: number;
    job_expense?: number;
    alimony?: number;
    child_support?: number;
    separate_maintenance_expense?: number;
}

// Financial Info
export interface FinancialInfo {
    CURRENT_EMPLOYMENT?: CurrentEmployment[];
    PREVIOUS_EMPLOYMENT?: PreviousEmployment[];
    MONTHLY_INCOME?: MonthlyIncome;
    MONTHLY_DEBT?: MonthlyDebt;
}

// Contact Info
export interface ContactInfo {
    home_phone?: string;
    work_phone?: string;
    cell_phone?: string;
    email?: string;
    preferred_contact_method?: string;
    work_phone_ext?: string;
}

// Reference
export interface Reference {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    relationship?: string;
    years_known?: number;
}

// Credit Report
export interface CreditReport {
    report_id: number;
    report_format: CreditReportFormat;
    encoding?: CreditReportEncoding;
    content?: string;
}

// Alias
export interface Alias {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
}

// Mortgage Base Applicant
export interface MortgageBaseApplicant {
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    m_initial?: string;
    suffix?: string;
    ssn?: string;
    dob?: string;
    ages_of_dependents?: string;
    mother_maiden_name?: string;
    citizenship?: string;
    member_number?: string;
    gender?: string;
    applicant_type?: string;
    school_months?: number;
    non_mcl_report_id?: string;
    is_VA_HUD_qualified?: BaseYN;
    interview_method?: InterviewMethod;

    CURRENT_ADDRESS?: CurrentAddress;
    PREVIOUS_ADDRESS?: CurrentAddress;
    FINANCIAL_INFO?: FinancialInfo;
    REFERENCE?: Reference[];
    CONTACT_INFO?: ContactInfo;
    DECLARATION?: MortgageDeclaration;
    MAILING_ADDRESS?: MailingAddress;
    ALIAS?: Alias[];
}

// Mortgage Applicant (extends Base)
export interface MortgageApplicant extends MortgageBaseApplicant {
    SPOUSE?: MortgageBaseApplicant;
    CREDIT_REPORT?: CreditReport;
    PRESENT_HOUSING_EXPENSE?: HousingExpense;
    is_fraud_credit?: BaseYN;
    is_ofac_credit?: BaseYN;
    combined_unsecured_aggregate_amount_with_lender?: number;
    combined_secured_aggregate_amount_with_lender_no_mortgage?: number;
}

// Mortgage Applicants container
export interface MortgageLoanApplicants {
    APPLICANT?: MortgageApplicant[];
    selected_product_borrower_id?: string;
}

// Trust Deed
export interface MortgageTrustDeed {
    position?: number;
    lien_holder_name?: string;
    monthly_payment?: number;
    unpaid_balance?: number;
    credit_limit?: number;
    is_to_be_paid_off?: BaseYN;
}

// Vesting
export interface Vesting {
    vesting_name: string;
    vesting_contact_index?: number;
    vesting_marital_status?: string;
    vesting_description?: string;
    vesting_is_on_app?: BaseYN;
}

// Home Equity Data
export interface HomeEquityData {
    description?: string;
    purpose_name?: string;
    home_equity_purpose_id?: string;
    other_reason_name?: string;
    home_equity_other_reason_id?: string;
    is_installment?: BaseYN;
}

// Loan Product Data
export interface LoanProductData {
    loan_repayment_type?: string;
    arm_rate_first_period_cap?: number;
    arm_rate_subsequent_adjust_months?: number;
    arm_rate_subsequent_periodic_cap?: number;
    index_type?: string;
    loan_term_due_months?: number;
    HELOC_max_balance_amount?: number;
    can_rate_rise?: BaseYN;
    can_balance_rise?: BaseYN;
    is_prepay_penalty?: BaseYN;
    can_payment_rise?: BaseYN;
}

// Requested Product
export interface MortgageRequestedProduct {
    loan_term?: number;
    rate_type?: RateType;
    description?: string;
    consumer_group_name?: string;
}

// Mortgage Loan Info
export interface MortgageLoanInfo {
    loan_purpose?: string;
    term_months?: number;
    estimated_property_value?: number;
    current_mortgage_balance?: number;
    amount_requested?: number;
    loan_term?: string;
    amount_approved?: number;
    rate?: number;
    monthly_payment?: number;
    apr?: number;
    rate_code?: string;
    occupancy_status?: string;
    interview_method?: string;
    reason?: string;

    PROPOSED_HOUSING_EXPENSE?: HousingExpense;
    CURRENT_TRUST_DEED?: MortgageTrustDeed[];
    NEW_TRUST_DEED?: MortgageTrustDeed[];
    VESTING?: Vesting[];
    VESTING_DESCRIPTION?: string;
    LEGAL_DESCRIPTION?: string;
    HOME_EQUITY_DATA?: HomeEquityData;
    LOAN_PRODUCT_DATA?: LoanProductData;
    REQUESTED_PRODUCT?: MortgageRequestedProduct;
}

// HELOC Card Holder
export interface HelocCardHolder {
    first_name?: string;
    last_name?: string;
    m_initial?: string;
    order_card?: BaseYN;
    reference_id?: string;
    fee?: string;
    is_borrower?: BaseYN;
    suffix?: string;
    ssn?: string;
    dob?: string;
    mother_maiden?: string;
    card_account_number?: string;
    mailing_address?: string;
    mailing_city?: string;
    mailing_state?: string;
    mailing_zip?: string;
}

// Property Info
export interface PropertyInfo {
    property_address?: string;
    property_city?: string;
    property_state?: string;
    property_zip?: string;
    property_county?: string;
    property_type?: string;
    number_of_units?: number;
    year_built?: number;
}

// Mortgage Insurance
export interface MortgageInsurance {
    type?: MortgageInsuranceType;
    is_selected?: BaseYN;
    premium?: number;
}

// Main Mortgage Loan class
export interface MortgageLoan {
    APPLICANTS?: MortgageLoanApplicants;
    LOAN_INFO?: MortgageLoanInfo;
    PROPERTY_INFO?: PropertyInfo;
    INSURANCE_OPTIONS?: MortgageInsurance[];
    HELOC_CARD_HOLDERS?: HelocCardHolder[];
    SYSTEM?: any[];
    version?: number;
}
